import styles from "./styles.module.scss";
import useProgramStripeIntegration from "app/utils/hooks/scheduling/useProgramStripeIntegration";
import Label from "../Label";
import React, { useState, useMemo } from "react";
import UserItems from "../UserItems";
import { schedulingFetchDelete, schedulingFetchPatch, schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import CancelRegistrationModal from "../CancelRegistrationModal";
import RefundConfirmationModal from "../RefundConfirmationModal";
import ManageRegistrationPaymentModal from "../ManageRegistrationPaymentModal";
import TransferRegistrantModal from "../TransferRegistrantModal";
import { isEmpty, some } from "lodash";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import CustomStripeProgramPrompt from "app/routes/Calendar/CustomStripeProgramPrompt";

// Helper function to wrap plain student profiles in StudentProgramRegistrationModel-compatible shape
const wrapStudentProfileForDisplay = (studentProfile: any): StudentProgramRegistrationModel => {
  const profile = studentProfile instanceof StudentProfileModel
    ? studentProfile
    : new StudentProfileModel(studentProfile);

  return new StudentProgramRegistrationModel(
    {
      id: 0, // Temporary ID for new registrations
      status: 'pending',
      createdAt: new Date().toISOString(),
      canRemove: true,
      paymentMethod: studentProfile.paymentMethod,
    },
    profile
  );
};

const RegistrantsAdmin = ({
  programModel,
  onRegistrationSuccess,
  onRegistrantDeleted,
  showRegistrantsWaitlisted,
  onTransferComplete,
}) => {
  const { t } = useTranslation();
  const [newParticipants, setNewParticipants] = useState([]);
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showStripePrompt, setShowStripePrompt] = useState(false);
  const [expiredRegistrationIds, setExpiredRegistrationIds] = useState<Set<number>>(new Set());
  const [showManagePayment, setShowManagePayment] = useState(false);
  const [managePaymentItem, setManagePaymentItem] = useState<StudentProgramRegistrationModel | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const allRegistrants = programModel.completedRegistrants;
  const allWaitlisted = programModel.waitlistedRegistrants;

  const registrants = allRegistrants?.filter(r => !expiredRegistrationIds.has(r.registrationId));
  const waitlisted = allWaitlisted?.filter(r => !expiredRegistrationIds.has(r.registrationId));
  const programId = programModel.id;
  const programStatus = programModel.status;
  const hasWaitlist = programModel.withWaitlist;
  const isViewOnly = false;

  const hasStripeIntegration = useProgramStripeIntegration(programModel);

  const removeNewParticipant = (registration: StudentProgramRegistrationModel) => {
    setNewParticipants((old) =>
      old.filter((participant) => participant.id !== registration.studentProfile.id)
    );
  };

  const handleRegistrationExpired = (registrationId: number) => {
    setExpiredRegistrationIds((prev) => new Set(prev).add(registrationId));
  };

  const handleRemovedPressed = (registration: StudentProgramRegistrationModel) => {
    setShowRemovePopup(true);
    setSelectedItem(registration);
  };

  // The refund/no-refund choice only makes sense when there is a captured online
  // payment to refund. Offline (and unpaid online) registrations get the simple
  // confirmation modal instead.
  const selectedIsOnlinePaid =
    selectedItem?.paymentMethod === 'online' &&
    (selectedItem?.paymentStatus === 'paid' || selectedItem?.paymentStatus === 'payment_confirmed');

  const handleParticipants = async () => {
    setShowStripePrompt(false);
    const newParticipantsIds = newParticipants?.map((item) => ({
      id: item.id,
      payment_method: item.paymentMethod
    }));
    if (isEmpty(newParticipantsIds)) {
      return;
    }
    const data = {
      student_profile_ids: newParticipantsIds,
    };

    setIsRegistering(true);
    try {
      const response = await schedulingFetchPost(`/programs/${programId}/register`, data);
      // Clear the input
      setNewParticipants([]);
      setShowStripePrompt(false);
      // Update parent state immediately
      await onRegistrationSuccess(response);
    } catch (error) {
      console.error('Registration error:', error);
      // Pass error to parent to display modal with error message
      if (error.errors) {
        await onRegistrationSuccess(error.errors);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleRemoved = async () => {
    if (!selectedItem || !selectedItem.registrationId) {
      console.error('Registration ID not found');
      toast.error(t("Could not identify the registration. Please try again."));
      setShowRemovePopup(false);
      return;
    }

    const paymentMethod = selectedItem.paymentMethod;
    const status = selectedItem.status;

    try {
      const response = await schedulingFetchDelete(`/student_program_registrations/${selectedItem.registrationId}`);
      setShowRemovePopup(false);
      onRegistrantDeleted(response);

      if (paymentMethod === 'online' && status === 'completed') {
        setShowRefundModal(true);
      }
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to delete registration:', error);
      if (error?.status === 422) {
        toast.error(error.error || t("Payment is currently being processed. Please try again shortly."));
      } else {
        toast.error(t("Failed to remove registrant. Please try again."));
      }
    }
  };

  const handleRemovedWithoutRefund = async () => {
    if (!selectedItem || !selectedItem.registrationId) {
      console.error('Registration ID not found');
      toast.error(t("Could not identify the registration. Please try again."));
      setShowRemovePopup(false);
      return;
    }

    try {
      const response = await schedulingFetchDelete(
        `/student_program_registrations/${selectedItem.registrationId}`,
        { skip_refund: true },
      );
      setShowRemovePopup(false);
      onRegistrantDeleted(response);
      setSelectedItem(null);
    } catch (error) {
      console.error('Failed to delete registration:', error);
      if (error?.status === 422) {
        toast.error(error.error || t("Payment is currently being processed. Please try again shortly."));
      } else {
        toast.error(t("Failed to remove registrant. Please try again."));
      }
    }
  };

  const handleMovePressed = (registration: StudentProgramRegistrationModel) => {
    setSelectedItem(registration);
    setShowTransferModal(true);
  };

  const handleTransferred = () => {
    setShowTransferModal(false);
    setSelectedItem(null);
    onTransferComplete?.();
  };

  const handleManagePayment = (registration: StudentProgramRegistrationModel) => {
    setManagePaymentItem(registration);
    setShowManagePayment(true);
  };

  const handleMarkAsPaid = async (registration: StudentProgramRegistrationModel) => {
    try {
      await schedulingFetchPatch(
        `/student_program_registrations/${registration.registrationId}`,
        { payment_status: "paid" }
      );
      toast.success(t("Payment marked as paid"));
      await onRegistrationSuccess(null);
    } catch (error) {
      console.error("Failed to mark registration as paid:", error);
      toast.error(t("An unexpected error has occurred."));
    }
  };

  const handleMarkAsUnpaid = async (registration: StudentProgramRegistrationModel) => {
    try {
      await schedulingFetchPatch(
        `/student_program_registrations/${registration.registrationId}`,
        { payment_status: "pending" }
      );
      toast.success(t("Payment marked as unpaid"));
      await onRegistrationSuccess(null);
    } catch (error) {
      console.error("Failed to mark registration as unpaid:", error);
      toast.error(t("An unexpected error has occurred."));
    }
  };

  const wrappedNewParticipants = useMemo(
    () =>
      newParticipants.map(p =>
        wrapStudentProfileForDisplay({
          ...p,
          paymentMethod: p.paymentMethod,
        })
      ),
    [newParticipants]
  );

  const shouldShowStripePrompt = () => {
    if (programModel.collectType === 'offline') {
      return false;
    }

    if (programModel.spotsRemaining === 0) {
      return false;
    }

    return some(newParticipants, p => p.paymentMethod === 'online');
  };

  // Free programs collect no payment, so the payment-status actions (Manage Payment,
  // Mark as Paid/Unpaid) are no-ops — hide them by withholding their handlers (CFTB-3266).
  const paymentHandlers = programModel.isFree
    ? {}
    : {
        onManagePayment: handleManagePayment,
        onMarkAsPaid: handleMarkAsPaid,
        onMarkAsUnpaid: handleMarkAsUnpaid,
      };

  return (
    <div className={styles.container}>
      {programStatus !== "completed" && !isViewOnly && (
        <>
          <div className={styles.registrants_selection_panel}>
            <button data-testid="save-edit"
              form="form-details"
              className={styles.confirm_button}
              type="submit"
              onClick={() => shouldShowStripePrompt() ? setShowStripePrompt(true) : handleParticipants()}
              style={{ display: newParticipants.length > 0 ? '' : 'none' }}
            >
              {t("Save Changes")}
            </button>
          </div>

          {newParticipants?.length > 0 && (
            <div className={styles.square_container}>
              <UserItems
                showEmail={true}
                showPaymentMethod={true}
                userList={wrappedNewParticipants}
                onClose={removeNewParticipant}
                allowRemove={true}
              />
            </div>
          )}
        </>
      )}
      {((registrants?.length > 0) || (waitlisted?.length > 0) || showRegistrantsWaitlisted) && (
        <div className={styles.line_container}>
          <div className={styles.square_container}>
            <Label
              text={
                programStatus === "completed"
                  ? `${t("Registered")} • ${registrants?.length}`
                  : `${t("Already Registered")} • ${registrants?.length || 0}`
              }
              textMarginLeft={15}
              item={(
                <div>
                  <div className={styles.line_separator} />
                  <UserItems
                    showEmail
                    showPaymentMethod
                    userList={registrants}
                    onClose={(item) => handleRemovedPressed(item)}
                    onExpired={handleRegistrationExpired}
                    allowRemove
                    useEllipsisMenu
                    {...paymentHandlers}
                    onMove={handleMovePressed}
                  />
                </div>
              )}
            />
          </div>
          {(hasWaitlist || showRegistrantsWaitlisted) && (
            <div className={styles.square_container}>
              <Label
                text={`${t("Waitlisted")} • ${waitlisted?.length || 0}`}
                textMarginLeft={15}
                item={(
                  <div>
                    <div className={styles.line_separator} />
                    <UserItems
                      showEmail
                      showPaymentMethod={true}
                      userList={waitlisted}
                      onClose={(item) => handleRemovedPressed(item)}
                      onExpired={handleRegistrationExpired}
                      allowRemove
                      forcePending
                      waitlisted={true}
                      useEllipsisMenu
                      {...paymentHandlers}
                      onMove={handleMovePressed}
                    />
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}

      {showStripePrompt && (
        <CustomStripeProgramPrompt
          data={{
            isOpen: showStripePrompt,
            title: t("Send the invoice to the athlete via Stripe?"),
            content: t("All registered athletes will receive invoices through Stripe."),
            confirmText: t("Send Invoices"),
            cancelText: t("Cancel"),
            confirmDanger: false,
            isRecurring: false,
            titleIcon: true,
            isLoading: isRegistering
          }}
          actions={{
            onCancel: () => setShowStripePrompt(false),
            onConfirm: handleParticipants,
          }}
        />
      )}

      <CancelRegistrationModal
        isOpen={showRemovePopup}
        studentName={`${selectedItem?.studentProfile?.firstName} ${selectedItem?.studentProfile?.lastName}`}
        onConfirm={handleRemoved}
        onCancel={() => setShowRemovePopup(false)}
        onCancelWithoutRefund={selectedIsOnlinePaid ? handleRemovedWithoutRefund : undefined}
      />

      {selectedItem && (
        <TransferRegistrantModal
          isOpen={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedItem(null);
          }}
          onTransferred={handleTransferred}
          registrationId={selectedItem.registrationId}
          studentName={`${selectedItem.studentProfile?.firstName} ${selectedItem.studentProfile?.lastName}`}
          sourcePrice={programModel.price}
          sourceIsWaitlisted={selectedItem.status === 'on_waitlist'}
        />
      )}

      <RefundConfirmationModal
        isOpen={showRefundModal}
        programName={programModel.title}
        onClose={() => setShowRefundModal(false)}
      />

      <ManageRegistrationPaymentModal
        registration={managePaymentItem}
        isOpen={showManagePayment}
        onClose={() => {
          setShowManagePayment(false);
          setManagePaymentItem(null);
        }}
        onSaved={async () => {
          await onRegistrationSuccess(null);
        }}
        collectType={programModel.collectType}
        stripeIntegrationEnabled={hasStripeIntegration}
      />
    </div>
  );
};

export default RegistrantsAdmin;
